// Calculate total of array values
export function calculateTotal(data, field) {
    if (!data || !Array.isArray(data)) return 0;
    return data.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
}

// Calculate average
export function calculateAverage(data, field) {
    if (!data || !Array.isArray(data) || data.length === 0) return 0;
    return calculateTotal(data, field) / data.length;
}

// Calculate percentage
export function calculatePercentage(part, total) {
    if (!total || total === 0) return 0;
    return (part / total) * 100;
}

// Calculate growth rate between two periods
export function calculateGrowth(current, previous) {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
}

// Count occurrences
export function countBy(data, field) {
    if (!data || !Array.isArray(data)) return {};

    return data.reduce((acc, item) => {
        const key = item[field];
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
}

// Sum by field value
export function sumBy(data, groupField, sumField) {
    if (!data || !Array.isArray(data)) return {};

    return data.reduce((acc, item) => {
        const key = item[groupField];
        acc[key] = (acc[key] || 0) + (Number(item[sumField]) || 0);
        return acc;
    }, {});
}

// Aggregate by category
export function aggregateByCategory(data, options = {}) {
    const { valueField = 'total', countField = 'id' } = options;

    if (!data || !Array.isArray(data)) return [];

    const grouped = data.reduce((acc, item) => {
        // Get category from item or from items array
        const categories = item.items
            ? item.items.map(i => i.categoryId)
            : [item.categoryId];

        categories.forEach(categoryId => {
            if (!categoryId) return;

            if (!acc[categoryId]) {
                const categoryItem = item.items?.find(i => i.categoryId === categoryId);
                acc[categoryId] = {
                    categoryId,
                    categoryName: categoryItem?.categoryName || categoryId,
                    count: 0,
                    total: 0,
                    items: [],
                };
            }

            acc[categoryId].count += 1;

            if (item.items) {
                const categoryItems = item.items.filter(i => i.categoryId === categoryId);
                acc[categoryId].total += categoryItems.reduce((sum, i) => sum + i.total, 0);
            } else {
                acc[categoryId].total += Number(item[valueField]) || 0;
            }
        });

        return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => b.total - a.total);
}

// Aggregate by time period
export function aggregateByTimePeriod(data, period = 'day', options = {}) {
    const { dateField = 'date', valueField = 'total' } = options;

    if (!data || !Array.isArray(data)) return [];

    const grouped = data.reduce((acc, item) => {
        const date = new Date(item[dateField]);
        let key;

        switch (period) {
            case 'day':
                key = item[dateField];
                break;
            case 'week':
                const weekStart = new Date(date);
                weekStart.setDate(date.getDate() - date.getDay());
                key = weekStart.toISOString().split('T')[0];
                break;
            case 'month':
                key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                break;
            case 'year':
                key = String(date.getFullYear());
                break;
            default:
                key = item[dateField];
        }

        if (!acc[key]) {
            acc[key] = {
                period: key,
                count: 0,
                total: 0,
                items: [],
            };
        }

        acc[key].count += 1;
        acc[key].total += Number(item[valueField]) || 0;
        acc[key].items.push(item);

        return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.period.localeCompare(b.period));
}

// Get top N items
export function getTopN(data, field, n = 10, order = 'desc') {
    if (!data || !Array.isArray(data)) return [];

    const sorted = [...data].sort((a, b) => {
        const aVal = Number(a[field]) || 0;
        const bVal = Number(b[field]) || 0;
        return order === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return sorted.slice(0, n);
}

// Get min and max
export function getMinMax(data, field) {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return { min: 0, max: 0 };
    }

    const values = data.map(item => Number(item[field]) || 0);
    return {
        min: Math.min(...values),
        max: Math.max(...values),
    };
}

// Calculate moving average
export function calculateMovingAverage(data, field, window = 7) {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    return data.map((item, index) => {
        const start = Math.max(0, index - window + 1);
        const windowData = data.slice(start, index + 1);
        const avg = calculateAverage(windowData, field);

        return {
            ...item,
            movingAverage: avg,
        };
    });
}

// Get unique values
export function getUniqueValues(data, field) {
    if (!data || !Array.isArray(data)) return [];
    return [...new Set(data.map(item => item[field]).filter(Boolean))];
}

// Group by field
export function groupBy(data, field) {
    if (!data || !Array.isArray(data)) return {};

    return data.reduce((acc, item) => {
        const key = item[field];
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});
}

export default {
    calculateTotal,
    calculateAverage,
    calculatePercentage,
    calculateGrowth,
    countBy,
    sumBy,
    aggregateByCategory,
    aggregateByTimePeriod,
    getTopN,
    getMinMax,
    calculateMovingAverage,
    getUniqueValues,
    groupBy,
};
